import React, { useEffect, useState } from "react";
import { Table, Typography } from "antd";
import dayjs from "dayjs";
import api from "../../../services/axiosInstance";
import GenericReportPage from "../../../components/common/GenericReportPage";
import { fmtMoney } from "../../../utils/reportExport";

const STATUS_OPTIONS = [
    { value: "", label: "All Status" },
    { value: "Draft", label: "Draft" },
    { value: "Pending Approval", label: "Pending" },
    { value: "Approved", label: "Approved" },
    { value: "Rejected", label: "Rejected" },
];

const GrnReport = () => {
    const [suppliers, setSuppliers] = useState([]);

    useEffect(() => {
        let mounted = true;
        api.get('/masterdropdown/listof_activesuppliers.php')
            .then((res) => { if (mounted) setSuppliers(res.data.dataset || []); })
            .catch(() => { /* Non-critical */ });
        return () => { mounted = false; };
    }, []);

    return (
        <GenericReportPage
            title="GRN Report"
            subtitle="Goods received notes with supplier, PO reference and amounts"
            breadcrumb={["Reports", "Inventory"]}
            fetchUrl="/reports/inventory/grn.php"
            rowKey="grn_id"
            filterDefs={[
                { type: "search", key: "search", label: "Search", col: 4, placeholder: "GRN ref, supplier, PO ref" },
                { type: "select", key: "sup_id", label: "Supplier", col: 4, options: suppliers, showSearch: true },
                { type: "select", key: "status", label: "Status", col: 4, options: STATUS_OPTIONS },
                { type: "date", key: "dateRange", label: "GRN Date", col: 6 },
            ]}
            columns={[
                { title: 'GRN Ref', dataIndex: 'grn_ref', key: 'grn_ref', width: 130, sorter: (a, b) => (a.grn_ref || '').localeCompare(b.grn_ref || '') },
                { title: 'Date', dataIndex: 'grn_date', key: 'grn_date', width: 110, render: (d) => dayjs(d).format("YYYY-MM-DD"), sorter: (a, b) => dayjs(a.grn_date).valueOf() - dayjs(b.grn_date).valueOf() },
                { title: 'Supplier', dataIndex: 'supplier_name', key: 'supplier_name', render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
                { title: 'PO Ref', dataIndex: 'po_ref', key: 'po_ref', width: 130, render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
                { title: 'Supplier Ref', dataIndex: 'sup_ref', key: 'sup_ref', width: 120, render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
                { title: 'Items', dataIndex: 'total_items_count', key: 'total_items_count', align: 'center', width: 70 },
                { title: 'Sub Total', dataIndex: 'grn_sub_total', key: 'grn_sub_total', align: 'right', width: 115, render: (v) => fmtMoney(v) },
                { title: 'Tax', dataIndex: 'grn_total_tax', key: 'grn_total_tax', align: 'right', width: 105, render: (v) => fmtMoney(v) },
                { title: 'Discount', dataIndex: 'grn_total_discount', key: 'grn_total_discount', align: 'right', width: 105, render: (v) => fmtMoney(v) },
                { title: 'Shipping', dataIndex: 'grn_total_shipping', key: 'grn_total_shipping', align: 'right', width: 105, render: (v) => fmtMoney(v) },
                { title: 'Final Amount', dataIndex: 'grn_final_amount', key: 'grn_final_amount', align: 'right', width: 125, sorter: (a, b) => (a.grn_final_amount || 0) - (b.grn_final_amount || 0), render: (v) => fmtMoney(v) },
                { title: 'Status', dataIndex: 'grn_status', key: 'grn_status', width: 130 },
                { title: 'Created By', dataIndex: 'creator_name', key: 'creator_name', width: 130 },
            ]}
            tableSummary={(pageData) => (
                <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={10}><b>Total ({pageData.length} rows)</b></Table.Summary.Cell>
                    <Table.Summary.Cell index={10} align="right"><b>{fmtMoney(pageData.reduce((s, r) => s + Number(r.grn_final_amount || 0), 0))}</b></Table.Summary.Cell>
                    <Table.Summary.Cell index={11} colSpan={2} />
                </Table.Summary.Row>
            )}
            pdf={{
                title: "GRN REPORT",
                subtitle: "Goods received notes with supplier, PO reference and amounts",
                columns: [
                    { header: "GRN Ref" }, { header: "Date" }, { header: "Supplier" }, { header: "PO Ref" },
                    { header: "Supplier Ref" }, { header: "Items", align: "center" }, { header: "Sub Total", align: "right" },
                    { header: "Tax", align: "right" }, { header: "Discount", align: "right" }, { header: "Shipping", align: "right" },
                    { header: "Final Amount", align: "right" }, { header: "Status" }, { header: "Created By" },
                ],
                rows: (rows) =>
                    rows.map((r) => [
                        r.grn_ref || "-",
                        dayjs(r.grn_date).format("YYYY-MM-DD"),
                        r.supplier_name || "-",
                        r.po_ref || "-",
                        r.sup_ref || "-",
                        String(r.total_items_count ?? ""),
                        fmtMoney(r.grn_sub_total),
                        fmtMoney(r.grn_total_tax),
                        fmtMoney(r.grn_total_discount),
                        fmtMoney(r.grn_total_shipping),
                        fmtMoney(r.grn_final_amount),
                        r.grn_status || "-",
                        r.creator_name || "-",
                    ]),
                totalRow: (rows) => [
                    "TOTAL", "", "", "", "", "", "", "", "", "",
                    fmtMoney(rows.reduce((s, r) => s + Number(r.grn_final_amount || 0), 0)), "", "",
                ],
                columnStyles: { 6: { halign: "right" }, 7: { halign: "right" }, 8: { halign: "right" }, 9: { halign: "right" }, 10: { halign: "right" } },
            }}
            excel={{
                filename: "grn-report",
                sheetName: "GRN",
                columns: [
                    { title: "GRN Ref", dataIndex: "grn_ref", width: 16 },
                    { title: "Date", dataIndex: "grn_date", width: 12 },
                    { title: "Supplier", dataIndex: "supplier_name", width: 24 },
                    { title: "PO Ref", dataIndex: "po_ref", width: 16 },
                    { title: "Supplier Ref", dataIndex: "sup_ref", width: 14 },
                    { title: "Items", dataIndex: "total_items_count", width: 8 },
                    { title: "Sub Total", dataIndex: "grn_sub_total", width: 14 },
                    { title: "Tax", dataIndex: "grn_total_tax", width: 12 },
                    { title: "Discount", dataIndex: "grn_total_discount", width: 12 },
                    { title: "Shipping", dataIndex: "grn_total_shipping", width: 12 },
                    { title: "Final Amount", dataIndex: "grn_final_amount", width: 14 },
                    { title: "Status", dataIndex: "grn_status", width: 16 },
                    { title: "Created By", dataIndex: "creator_name", width: 18 },
                ],
                rows: (rows) =>
                    rows.map((r) => ({
                        ...r,
                        grn_date: dayjs(r.grn_date).format("YYYY-MM-DD"),
                        grn_sub_total: fmtMoney(r.grn_sub_total),
                        grn_total_tax: fmtMoney(r.grn_total_tax),
                        grn_total_discount: fmtMoney(r.grn_total_discount),
                        grn_total_shipping: fmtMoney(r.grn_total_shipping),
                        grn_final_amount: fmtMoney(r.grn_final_amount),
                    })),
                extraRows: (rows) => [["TOTAL", "", "", "", "", "", "", "", "", "", fmtMoney(rows.reduce((s, r) => s + Number(r.grn_final_amount || 0), 0)), "", ""]],
            }}
        />
    );
};

export default GrnReport;
